import { StyleSheet, View, ViewProps } from 'react-native'
import { useBuilding, useCompany, useFloorplan, useLocation } from 'zens-store'
import { useTranslation } from 'react-i18next'
import { useBreadcrumb } from '@dms/hook'
import { Color, Font, Icon, PopUp, Spacing } from 'zens-storybook'
import { useState } from 'react'

import { LayoutHeader } from '../layout'
import { MenuMore } from '../menu'
import { FloorplanDownload } from '../floorplan'

import { LabelType } from './floorplan'

const styles = StyleSheet.create({
  container: {},
  more: {
    backgroundColor: Color.simplyWhite,
    borderBottomColor: Color.calmGrey,
    borderBottomWidth: 1,
  },
  moreTitle: {
    fontFamily: Font.medium,
  },
  icon: {
    width: 16,
    marginRight: Spacing.XS,
  },
})

interface Props extends ViewProps {
  companyId: number
  locationId: number
  buildingId: number
  floorId: number
  labels?: LabelType
  onChangeLabels?: (labels?: LabelType) => void
}

const FloorplanHeader = (props: Props) => {
  const { companyId, locationId, buildingId, floorId, labels, onChangeLabels, style, ...otherProps } = props
  const { t } = useTranslation()
  const [showDownload, setShowDownload] = useState(false)
  const company = useCompany({ companyId })
  const location = useLocation({ companyId, locationId })
  const building = useBuilding({ companyId, buildingId })
  const floorplan = useFloorplan({ companyId, floorId })

  const breadcrumb = useBreadcrumb([
    {
      title: company.data?.name || '',
      href: `/companies/${companyId}`,
    },
    {
      title: location.data?.name || '',
      href: `/companies/${companyId}/locations/${locationId}`,
    },
    {
      title: building.data?.name || '',
      href: `/companies/${companyId}/locations/${locationId}/buildings/${buildingId}`,
    },
    {
      title: floorplan.data?.name || `${t('Floor')} ${floorplan.data?.number}`,
    },
  ])

  function more(key: string | string[]) {
    switch (key) {
      case 'name':
      case 'serialnumber':
        if (labels === key) onChangeLabels?.(undefined)
        else onChangeLabels?.(key)
        break
      case 'download':
        setShowDownload(true)
      // try {
      //   saveSvg(`${t('Floorplan')}.svg`, 'FloorplanView', 'image/svg+xml')
      // } catch (error) {
      //   message.add({ message: t('Error generating heatmap'), status: 'error' })
      //   console.debug('Error generating heatmap', error)
      // }
    }
  }

  return (
    <>
      <LayoutHeader
        breadcrumb={breadcrumb}
        title={`${t('Floorplan')} - ${floorplan.data?.name || `${t('Floor')} ${floorplan.data?.number}`}`}
        style={[styles.container, style]}
        accessory={
          <MenuMore
            items={[
              {
                key: 'show',
                title: t('View options'),
                disabled: true,
                style: styles.more,
                textStyle: styles.moreTitle,
              },
              {
                key: 'name',
                icon:
                  labels === 'name' ? (
                    <Icon name="checkmark" size={14} style={styles.icon} />
                  ) : (
                    <View style={styles.icon} />
                  ),
                title: t('Workspace name'),
                testID: 'FloorFloorplan.WorkspaceName',
              },
              {
                key: 'serialnumber',
                icon:
                  labels === 'serialnumber' ? (
                    <Icon name="checkmark" size={14} style={styles.icon} />
                  ) : (
                    <View style={styles.icon} />
                  ),
                title: t('Product serial number'),
                testID: 'FloorFloorplan.ProductSerialNumber',
              },
              {
                key: 'title',
                title: t('Download options'),
                disabled: true,
                style: styles.more,
                textStyle: styles.moreTitle,
              },
              {
                key: 'download',
                icon: 'download',
                title: t('Download floorplan'),
                testID: 'FloorFloorplan.Download',
              },
            ]}
            onSelect={more}
            testID="FloorFloorplan.More"
          />
        }
        {...otherProps}
      />
      <PopUp visible={showDownload} onClose={() => setShowDownload(false)}>
        <FloorplanDownload
          companyId={companyId}
          floorId={floorId}
          labels={labels}
          onCancel={() => setShowDownload(false)}
          onDownload={() => setShowDownload(false)}
        />
      </PopUp>
    </>
  )
}

export default FloorplanHeader
